import React, { useCallback, useEffect, useMemo } from 'react';
import Immutable from 'immutable';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { useQuizContext, type QuizContextProps, type QuizQuestion } from '../../../../context/QuizContext';
import { useWebformContext } from '../../../../context/WebformContext';
import WebformAssessmentActionsInner from '../../Assessment/Actions/Inner';
import type { WebformWizardPage } from '../../types';

export interface WebformQuizActionsProps {
  entity: ImmutableEntity<WebformWizardPage>;
  pages: Immutable.Map<string, string>;
  currentPage: string;
  currentQuizQuestion: QuizQuestion;
  onPreviousPage: () => void;
  onNextPage: () => void;
  handleSubmit: () => void;
  updateQuizQuestion: QuizContextProps['updateQuizQuestion'];
}

const WebformQuizActionsInner: React.FC<WebformQuizActionsProps> = (props) => {
  const {
    entity,
    pages: wizardPages,
    currentPage: currentWizardPage,
    currentQuizQuestion,
    onPreviousPage: onPreviousWizardPage,
    onNextPage: onNextWizardPage,
    handleSubmit,
    updateQuizQuestion,
  } = props;

  const {
    id,
    currentPage,
    showAnswerOnNewPage,
    givenAnswers,
  } = currentQuizQuestion;

  const { updateCurrentPage } = useWebformContext();

  // If we're both on the last wizard page and last question page,
  // the next button is a submit button, otherwise it is a next button.
  const showSubmitButton = useMemo(() => {
    const isOnLastQuestionPage = (showAnswerOnNewPage && currentPage === 'answer') || !showAnswerOnNewPage;
    return currentWizardPage === wizardPages.last() && isOnLastQuestionPage;
  }, [currentPage, currentWizardPage, showAnswerOnNewPage, wizardPages]);

  // Moves to the 'question' page if we're currently on the 'answer'
  // page. Otherwise, use the previous wizard page event handler to
  // move to the previous question.
  const onPreviousQuestionPage = useCallback(() => {
    if (currentPage === 'answer') {
      updateQuizQuestion({ id, currentPage: 'question' });
      return;
    }

    onPreviousWizardPage();
  }, [currentPage, id, onPreviousWizardPage, updateQuizQuestion]);

  // Moves to the 'answer' page if we show the answer on a new page and
  // we're currently on the 'question' page. Otherwise, use the next wizard
  // page event handler to move to the next question.
  const onNextQuestionPage = useCallback(() => {
    if (showSubmitButton) {
      handleSubmit();
      return;
    }

    if (showAnswerOnNewPage && currentPage === 'question') {
      updateQuizQuestion({ id, currentPage: 'answer' });
      return;
    }

    onNextWizardPage();
  }, [
    currentPage,
    handleSubmit,
    id,
    onNextWizardPage,
    showAnswerOnNewPage,
    showSubmitButton,
    updateQuizQuestion,
  ]);

  useEffect(() => {
    updateCurrentPage(currentWizardPage);
  }, [currentWizardPage, updateCurrentPage]);

  return (
    <WebformAssessmentActionsInner
      prevButtonLabel={entity.get('#wizard_prev__label')}
      isPrevButtonDisabled={currentWizardPage === wizardPages.first() && currentPage === 'question'}
      nextButtonLabel={entity.get('#wizard_next__label')}
      isNextButtonDisabled={givenAnswers.length === 0}
      showSubmitButton={showSubmitButton}
      submitButtonLabel={entity.get('#submit__label')}
      onPreviousPage={onPreviousQuestionPage}
      onNextPage={onNextQuestionPage}
    />
  );
};

const WebformQuizActions: React.FC<Omit<WebformQuizActionsProps, 'currentQuizQuestion' | 'updateQuizQuestion'>> = (props) => {
  const { currentQuizQuestion, updateQuizQuestion } = useQuizContext();

  if (!currentQuizQuestion) {
    return null;
  }

  return (
    <WebformQuizActionsInner
      {...props}
      currentQuizQuestion={currentQuizQuestion}
      updateQuizQuestion={updateQuizQuestion}
    />
  );
};

export default WebformQuizActions;
